import React from "react";
import { Link } from "../../atoms/link/link";
import {
  ChannelLineup,
  type ChannelLineupProps
} from "./channel-lineup";
import { SearchableGenrePackList } from "./genre-pack-list.client";
import { SearchableGridHeader } from "./grid-header.client";
import { LineupAnalytics } from "./lineup-analytics.client";
import { LineupFooter } from "./lineup-footer";
import { LineupMarketProvider } from "./lineup-market.client";
import { LineupSearch, LineupSearchSourceProvider } from "./lineup-search.client";
import type { LineupSearchSource } from "./lineup-search.shared";
import {
  LineupSelectedView,
  LineupSortRadios,
  LineupSortedGrid,
  LineupViewProvider,
  LineupViewToggle
} from "./lineup-view.client";
import {
  SORT_VALUE_AZ,
  prepareSortOptions,
  resolveSelectedSort
} from "./sort-radios.shared";
import { prepareViewToggleOptions } from "./view-toggle.shared";
import { LINK_POSITION_BODY } from "../../services/analytics";
import { isSafeHref } from "../../services/safe-href";
import type { LineupRowCopy } from "./row-copy.shared";
import { sortOptionsOf, viewToggleOptionsOf } from "./lineup-controls";
import { resolveLineupForBlok, type LineupBlok } from "./lineup-seam";
import { withSafeRender } from "../../atoms/safe-render";
import { MYHOME_TEAM_CODE } from "./myhome-team.shared";

/** `DDS_CHANNEL_LINEUP` on the v3 canvas — the composition site for the Channel Lineup organism. */

/** PG6 has no resolve, so the search offers nothing. */
const EMPTY_SEARCH_SOURCE: LineupSearchSource = {};

/**
 * Every field the `dds_channel_lineup` blok authors, flat at the root (the DDS prop-shape rule).
 * Only repeatable lists nest; a prefix appears only where two parts of the lineup would otherwise
 * share a name.
 */
export interface ChannelLineupBlokFields
  extends Pick<
    ChannelLineupProps,
    "back_to_top_label" | "error_message_content" | "heading_content" | "heading_level"
  > {
  /** GS1 — the MyFree section's name. */
  grid_myfree_heading_content?: string;
  /** GS1 — the subscriptions section's heading. */
  grid_subscriptions_heading_content?: string;
  /** GS1 — the included channels section's heading. */
  grid_included_heading_content?: string;
  /** GS1/GS3 — the add-on section's heading. */
  grid_addon_heading_content?: string;
  /** GS3 — the Category view's local channels heading. */
  grid_local_heading_content?: string;
  /** GS3 — the Category view's regional sports heading. */
  grid_rsn_heading_content?: string;

  /** VT7 — the view toggle's accessible name. */
  view_toggle_aria_label?: string;
  /** GH4 — the sort group's accessible name. */
  sort_aria_label?: string;

  /** GH1 — the grid header's left-cell wording. */
  channels_label?: string;
  /** GH1 — the wording beside the sort control. */
  sort_label?: string;

  /** SR1 — the search prompt, and the field's accessible name. */
  search_field_label?: string;
  /** Names the search landmark. */
  search_aria_label?: string;
  /** The search ✕'s accessible name. */
  search_clear_aria_label?: string;
  /** SR2 — shown at exactly one character typed. */
  search_typing_hint_content?: string;
  /** SR5 — the select-all row's prefix. */
  search_select_all_prefix_label?: string;
  /** SR5 — the heading channel suggestions sit under. */
  search_group_heading_content?: string;
  /** SR9 — the suggestion list's no-match message. */
  search_empty_message_content?: string;
  /** SR8 — written, a Reset control renders. */
  search_reset_label?: string;
  /** AX11 — the verb in a chip's remove button. */
  search_chip_remove_aria_label?: string;
  /** SR7 — the chip overflow toggle's labels. */
  search_show_more_label?: string;
  search_show_less_label?: string;
  /** SR10 — the selection limit. */
  search_max_selections?: number;
  /** GQ3 — the "Search results" heading in the Signature view. */
  search_heading_content?: string;
  /** GQ6 — the partial-results message. */
  search_partial_message_content?: string;
  /** GQ6 — the failed-search message. */
  search_error_message_content?: string;
  /** SR4 — the heading subscription suggestions sit under. */
  search_subscriptions_heading_content?: string;
  /** GQ4 — the note on an add-on search result row. */
  search_addon_note_label?: string;

  /** AX3 — each cell state's screen-reader wording. */
  included_state_label?: string;
  addon_state_label?: string;
  promo_state_label?: string;
  not_included_label?: string;
  /** GR3 — each indicator tooltip's prefix, before the package name. */
  included_tooltip_prefix_label?: string;
  addon_tooltip_prefix_label?: string;
  promo_tooltip_prefix_label?: string;
  /** GR5 — the RSN See-details toggle and the prefix before its team names. */
  rsn_details_more_label?: string;
  rsn_details_less_label?: string;
  rsn_teams_label?: string;
  /** GR4/GP7 — the locality badge wording per kind, in the grid, the packs and the search. */
  local_badge_label?: string;
  rsn_badge_label?: string;
  national_badge_label?: string;
  /** GR4/GP7 — the badge ⓘ text per kind; absent, no ⓘ. */
  local_badge_tooltip_content?: string;
  rsn_badge_tooltip_content?: string;
  national_badge_tooltip_content?: string;

  /** GR5 — the RSN legal note when a tier has none of its own. */
  rsn_legal_default_content?: string;
  /** AX6 — channel-name tooltips on the row logos. */
  show_channel_logo_tooltip?: boolean;
  /** GS11 — the MyFree section's logo and Sign up button. */
  myfree_logo_url?: string;
  myfree_cta_label?: string;
  myfree_cta_url?: string;
  /** GS11 — the Sign up button's accessible name, as authored; absent, its wording names it. */
  myfree_cta_aria_label?: string;
  /** GS9 — the three legend lines. */
  legend_included_content?: string;
  legend_addon_content?: string;
  legend_promo_content?: string;
  /** GS6/GQ5 — the Signature view's nothing-to-show message. */
  grid_empty_message_content?: string;

  /** GP5/GP6 — the per-pack row headings. */
  genre_subscriptions_heading_content?: string;
  genre_channels_heading_content?: string;
  /** GP12 — the pack expand/collapse labels. */
  genre_show_more_label?: string;
  genre_show_less_label?: string;
  /** GP9 — the MyHome Team pack's link to its teams pop-up. Needs both, or no link. */
  myhome_team_link_label?: string;
  myhome_team_link_url?: string;
  /** GP11 — the Genre Packs load-failure message and its PDF link. */
  genre_error_message_content?: string;
  genre_error_pdf_url?: string;
  genre_error_pdf_link_label?: string;
  /** GP10 — the Genre Packs no-match message. */
  genre_search_empty_message_content?: string;

  /** FT1 — the channel-list PDF and its link text. */
  footer_pdf_url?: string;
  footer_pdf_link_label?: string;
  /** FT2 — the satellite button. */
  satellite_cta_label?: string;
  satellite_cta_url?: string;
  /** FT2 — the satellite button's accessible name, as authored; absent, its wording names it. */
  satellite_cta_aria_label?: string;
}

export interface ChannelLineupSectionProps extends ChannelLineupBlokFields, LineupBlok {
  /**
   * ZP1/ZP2/GP13 — the working ZIP Code control, built and supplied by the page. Placed twice from
   * one node: the page-level ZIP line and the MyHome Team pack's own; both write one page-wide
   * store.
   */
  zipSlot?: React.ReactNode;
  /** GR5 — per-tier RSN legal notes from `packagesConfig.rsnTierLegalText`,
   *  attached by `attachLineupData`. Page-set, never authored; absent, RSN rows
   *  fall back to `rsn_legal_default_content`. */
  tierLegalNotes?: Record<number, string>;
}

function ChannelLineupSectionImpl({
  back_to_top_label: backToTopLabel,
  error_message_content: errorMessageContent,
  heading_content: headingContent,
  heading_level: headingLevel,
  grid_subscriptions_heading_content: gridSubscriptionsHeadingContent,
  grid_included_heading_content: gridIncludedHeadingContent,
  grid_addon_heading_content: gridAddonHeadingContent,
  grid_local_heading_content: gridLocalHeadingContent,
  grid_rsn_heading_content: gridRsnHeadingContent,
  view_toggle_aria_label: viewToggleAriaLabel,
  sort_aria_label: sortAriaLabel,
  channels_label: channelsLabel,
  sort_label: sortLabel,
  search_field_label: searchFieldLabel,
  search_aria_label: searchAriaLabel,
  search_clear_aria_label: searchClearAriaLabel,
  search_typing_hint_content: searchTypingHintContent,
  search_select_all_prefix_label: searchSelectAllPrefixLabel,
  search_group_heading_content: searchGroupHeadingContent,
  search_empty_message_content: searchEmptyMessageContent,
  search_reset_label: searchResetLabel,
  search_chip_remove_aria_label: searchChipRemoveAriaLabel,
  search_show_more_label: searchShowMoreLabel,
  search_show_less_label: searchShowLessLabel,
  search_max_selections: searchMaxSelections,
  search_heading_content: searchHeadingContent,
  search_partial_message_content: searchPartialMessageContent,
  search_error_message_content: searchErrorMessageContent,
  search_subscriptions_heading_content: searchSubscriptionsHeadingContent,
  search_addon_note_label: searchAddonNoteLabel,
  included_state_label: includedStateLabel,
  addon_state_label: addonStateLabel,
  promo_state_label: promoStateLabel,
  not_included_label: notIncludedLabel,
  included_tooltip_prefix_label: includedTooltipPrefixLabel,
  addon_tooltip_prefix_label: addonTooltipPrefixLabel,
  promo_tooltip_prefix_label: promoTooltipPrefixLabel,
  rsn_details_more_label: rsnDetailsMoreLabel,
  rsn_details_less_label: rsnDetailsLessLabel,
  rsn_teams_label: rsnTeamsLabel,
  local_badge_label: localBadgeLabel,
  rsn_badge_label: rsnBadgeLabel,
  national_badge_label: nationalBadgeLabel,
  local_badge_tooltip_content: localBadgeTooltipContent,
  rsn_badge_tooltip_content: rsnBadgeTooltipContent,
  national_badge_tooltip_content: nationalBadgeTooltipContent,
  myhome_team_link_label: myhomeTeamLinkLabel,
  myhome_team_link_url: myhomeTeamLinkUrl,
  rsn_legal_default_content: rsnLegalDefaultContent,
  show_channel_logo_tooltip: showChannelLogoTooltip,
  myfree_logo_url: myfreeLogoUrl,
  myfree_cta_label: myfreeCtaLabel,
  myfree_cta_url: myfreeCtaUrl,
  myfree_cta_aria_label: myfreeCtaAriaLabel,
  legend_included_content: legendIncludedContent,
  legend_addon_content: legendAddonContent,
  legend_promo_content: legendPromoContent,
  grid_empty_message_content: gridEmptyMessageContent,
  genre_subscriptions_heading_content: genreSubscriptionsHeadingContent,
  genre_channels_heading_content: genreChannelsHeadingContent,
  genre_show_more_label: genreShowMoreLabel,
  genre_show_less_label: genreShowLessLabel,

  genre_error_message_content: genreErrorMessageContent,
  genre_error_pdf_url: genreErrorPdfUrl,
  genre_error_pdf_link_label: genreErrorPdfLinkLabel,
  genre_search_empty_message_content: genreSearchEmptyMessageContent,
  footer_pdf_url: footerPdfUrl,
  footer_pdf_link_label: footerPdfLinkLabel,
  satellite_cta_label: satelliteCtaLabel,
  satellite_cta_url: satelliteCtaUrl,
  satellite_cta_aria_label: satelliteCtaAriaLabel,
  tierLegalNotes,
  zipSlot,
  ...blok
}: ChannelLineupSectionProps) {
  // One line, and the whole data layer sits behind it. See `lineup-seam.ts` — `null` is PG6, the
  // cold-model error for the lineup as a whole, and the only defined failure (DATA-CONTRACT §5).
  const lineup = resolveLineupForBlok(blok);
  const hasFooter = [footerPdfUrl, footerPdfLinkLabel, satelliteCtaLabel, satelliteCtaUrl].some(
    value => value !== undefined
  );

  /* VT1 and GH4's option lists, normalised out of what Storyblok publishes and then validated by
     the components' own preparers. */
  const viewOptions = prepareViewToggleOptions(viewToggleOptionsOf(blok.view_options)).renderable;
  const sortOptions = prepareSortOptions(sortOptionsOf(blok.sort_options)).renderable;

  /* Every row's authored wording in one object, built once per page render so a memoised row
     compares one reference. */
  const rowCopy: LineupRowCopy = {
    stateLabels: {
      included: includedStateLabel,
      addon: addonStateLabel,
      promo: promoStateLabel,
      none: notIncludedLabel
    },
    tooltipPrefixes: {
      included: includedTooltipPrefixLabel,
      addon: addonTooltipPrefixLabel,
      promo: promoTooltipPrefixLabel
    },
    badgeLabels: {
      local: localBadgeLabel,
      rsn: rsnBadgeLabel,
      national: nationalBadgeLabel
    },
    badgeTooltips: {
      local: localBadgeTooltipContent,
      rsn: rsnBadgeTooltipContent,
      national: nationalBadgeTooltipContent
    },
    detailsMoreLabel: rsnDetailsMoreLabel,
    detailsLessLabel: rsnDetailsLessLabel,
    teamsLabel: rsnTeamsLabel,
    addonNoteLabel: searchAddonNoteLabel
  };
  const searchItemOptions = {
    badgeLabels: rowCopy.badgeLabels,
    subscriptionsGroupHeading: searchSubscriptionsHeadingContent
  };
  const myhomeLabel = (myhomeTeamLinkLabel ?? "").trim();
  const myhomeHref = (myhomeTeamLinkUrl ?? "").trim();
  /* GH4 — the authored sort clamped to an option that renders. Without this a blok naming a sort
     nobody authored an option for leaves the radio group with nothing checked, which is a control
     that looks broken rather than one that defaulted. */
  const initialSort = resolveSelectedSort(sortOptions, lineup?.sort) ?? SORT_VALUE_AZ;

  /* GP11 is not spelt here. */
  let selectedViewSlot: React.ReactNode | null = null;
  if (lineup) {
    /* GP9 / GP13 — the two things only the MyHome Team pack carries, built here and handed down
       keyed by its billing code. */
    const myHomeTeamSlots = {
      [MYHOME_TEAM_CODE]: {
        footerSlot: myhomeLabel !== "" && isSafeHref(myhomeHref) ?
          (
            /* `linkAction="iframe_modal"` needs the page's one `IframeModal`, mounted per v3 page in
               `app/v3/[...slug]/layout.tsx`. AN6 reports the label lower-cased. */
            <Link
              href={myhomeHref}
              linkAction="iframe_modal"
              linkName={myhomeLabel.toLowerCase()}
              linkPosition={LINK_POSITION_BODY}
            >
              {myhomeLabel}
            </Link>
          ) :
          undefined,
        zipSlot
      }
    };

    /* Both views are built; a view mounts when first chosen. VT3 switches without a reload, so the
       choice cannot be made here — `LineupSelectedView` makes it from the live view and keeps a
       shown view mounted. */
    selectedViewSlot = (
      <LineupSelectedView
        genreSlot={
          <SearchableGenrePackList
            channels_heading_content={genreChannelsHeadingContent}
            error_message_content={genreErrorMessageContent}
            error_pdf_link_label={genreErrorPdfLinkLabel}
            error_pdf_url={genreErrorPdfUrl}
            local_badge_label={localBadgeLabel}
            local_badge_tooltip_content={localBadgeTooltipContent}
            national_badge_label={nationalBadgeLabel}
            national_badge_tooltip_content={nationalBadgeTooltipContent}
            packSlots={myHomeTeamSlots}
            /* The MyFree pack's package-config row has no billing code, so it
               resolves no CTA (G10). It takes the authored MyFree CTA the
               Signature lockup uses; none authored, no button. */
            packs={lineup.packs.map(pack => (pack.kind === "genreAddon" && !pack.ctaHref ?
                {
                  ...pack,
                  ctaLabel: myfreeCtaLabel,
                  ctaHref: myfreeCtaUrl,
                  ctaAriaLabel: myfreeCtaAriaLabel
                } :
                pack))}
            rsn_badge_label={rsnBadgeLabel}
            rsn_badge_tooltip_content={rsnBadgeTooltipContent}
            search_empty_message_content={genreSearchEmptyMessageContent}
            show_less_label={genreShowLessLabel}
            show_more_label={genreShowMoreLabel}
            subscriptions_heading_content={genreSubscriptionsHeadingContent}
          />
        }
        signatureSlot={
          <>
            {/* GH5 — `SearchableGridHeader`, never the bare `GridHeader`. The header has to hide the sort control while a search is active and only something reading the live selection can tell it to; the bare component renders correctly and leaves GH5 dead. */}
            <SearchableGridHeader
              channels_label={channelsLabel}
              columns={lineup.columns}
              sort_label={sortLabel}
              sortSlot={sortOptions.length > 0 ?
                <LineupSortRadios aria_label={sortAriaLabel} options={sortOptions} /> :
                null}
            />
            {/* Without this, browse mode emits H1 -> H3. Two defaults collide — the root draws its heading `h1`, `signature-grid.tsx` draws section headings `h3` — and neither may move, because the search branch reads this same value (`signature-grid.client.tsx:218` -> `SearchResults`, whose own default is already `h2`). */}
            <LineupSortedGrid
              addonHeading={gridAddonHeadingContent}
              categories={lineup.categories}
              channels={lineup.channels}
              columns={lineup.columns}
              empty_message_content={gridEmptyMessageContent}
              includedHeading={gridIncludedHeadingContent}
              localHeading={gridLocalHeadingContent}
              legend_addon_content={legendAddonContent}
              legend_included_content={legendIncludedContent}
              legend_promo_content={legendPromoContent}
              myFreeLabel={lineup.myFreeLabel}
              myfree_cta_aria_label={myfreeCtaAriaLabel}
              myfree_cta_label={myfreeCtaLabel}
              myfree_cta_url={myfreeCtaUrl}
              myfree_logo_url={myfreeLogoUrl}
              platform={lineup.platform}
              rowCopy={rowCopy}
              rsnHeading={gridRsnHeadingContent}
              rsn_legal_default_content={rsnLegalDefaultContent}
              search_error_message_content={searchErrorMessageContent}
              search_heading_content={searchHeadingContent}
              search_partial_message_content={searchPartialMessageContent}
              sectionHeadingLevel="h2"
              show_channel_logo_tooltip={showChannelLogoTooltip}
              subscriptionRows={lineup.subscriptionRows}
              subscriptionsHeading={gridSubscriptionsHeadingContent}
              tierLegalNotes={tierLegalNotes}
            />
          </>
        }
      />
    );
  }

  return (
    /* Locals and RSNs arrive in the browser after load (the page publishes them here), so
       the server render never waits on beam. */
    <LineupMarketProvider
      columnCodes={lineup?.columns.map(column => column.billingProductCode) ?? []}
      platform={lineup?.platform ?? "ott"}
    >
      {/* Still wrapped on the PG6 branch: the root draws `searchSlot` either way, and `LineupSearch` THROWS outside a provider. */}
      <LineupSearchSourceProvider
        itemOptions={searchItemOptions}
        source={lineup?.searchSource ?? EMPTY_SEARCH_SOURCE}
      >
        {/* VT4 — the search provider is outside this one, so a view switch never unmounts the selection and the chips survive it. */}
        <LineupViewProvider initialSort={initialSort} initialView={lineup?.view ?? "signature"}>
          {/* AN1's page load and AN2's per-view product impression. Renders nothing; it reads the live view, which is why it is here and not on the server. A sibling of the view, never a child of it. */}
          <LineupAnalytics genrePacks={lineup?.packs} signaturePackages={lineup?.columns} />
          <ChannelLineup
            back_to_top_label={backToTopLabel}
            error_message_content={errorMessageContent}
            /* FT1/FT2 — drawn below the selected view, and on the PG6 branch too: FT2 is a usable
               exit from a lineup that could not be built. */
            footerSlot={hasFooter ?
              (
                <LineupFooter
                  pdf_link_label={footerPdfLinkLabel}
                  pdf_url={footerPdfUrl}
                  satellite_cta_aria_label={satelliteCtaAriaLabel}
                  satellite_cta_label={satelliteCtaLabel}
                  satellite_cta_url={satelliteCtaUrl}
                />
              ) :
              null}
            heading_content={headingContent}
            heading_level={headingLevel}
            searchSlot={(
              <LineupSearch
                aria_label={searchAriaLabel}
                chip_remove_aria_label={searchChipRemoveAriaLabel}
                clear_aria_label={searchClearAriaLabel}
                empty_message_content={searchEmptyMessageContent}
                field_label={searchFieldLabel}
                group_heading_content={searchGroupHeadingContent}
                max_selections={searchMaxSelections}
                reset_label={searchResetLabel}
                select_all_prefix_label={searchSelectAllPrefixLabel}
                show_less_label={searchShowLessLabel}
                show_more_label={searchShowMoreLabel}
                typing_hint_content={searchTypingHintContent}
              />
            )}
            selectedViewSlot={selectedViewSlot}
            /* The first paint's view, not the live one. The root is a Server Component and drives
               only PG3's genre-only intro row off this, which this composition passes none of — so it
               is inert today. */
            view={lineup?.view ?? "signature"}
            /* VT1's control, bound to the view `resolveInitialView` chose for this request. */
            viewToggleSlot={lineup ?
              <LineupViewToggle aria_label={viewToggleAriaLabel} options={viewOptions} /> :
              null}
            /* ZP1/ZP2/ZP3/ZP13 — the working ZIP Code, the same node the two v3 carousels put in
               their own `zip_code` slot. Its page-wide store keeps GP13 true across instances. */
            zipSlot={lineup ? zipSlot : null}
          />
        </LineupViewProvider>
      </LineupSearchSourceProvider>
    </LineupMarketProvider>
  );
}
export const ChannelLineupSection = withSafeRender(ChannelLineupSectionImpl, "ChannelLineupSection");

export default ChannelLineupSection;
